import type {
  TableColumn,
  TableRow,
} from "@/queries/data-center/functional-core.api";
import { cn } from "@/utils/theme";
import { Icon } from "@elaniintc/goes-design-system";

/**
 * Get the column display name
 *
 * @param type
 * @returns
 */
const getColumnDisplayName = (type: string) => {
  switch (type.toLowerCase()) {
    case "string":
    case "uuid":
      return "texto";
    case "integer":
      return "número";
    case "int8":
      return "INT8";
    case "float8":
      return "FLOAT8";
    case "boolean":
      return "verdadero/falso";
    default:
      if (type.toLowerCase().includes("character")) {
        return "texto";
      }
      if (type.toLowerCase().includes("timestamp")) {
        return "fecha";
      }

      return type.toUpperCase();
  }
};

const getSortIcon = (
  direction: ReadOnlyTableSortType,
  isColumnSelected: boolean,
) => {
  if (direction === "asc" && isColumnSelected) return "sort-up";
  if (direction === "desc" && isColumnSelected) return "sort-down";
  return "sort";
};

export type ReadOnlyTableSortType = "asc" | "desc" | "off";

export type ReadOnlyTableSortState = {
  columnId: string;
  direction: ReadOnlyTableSortType;
};

interface ReadOnlyTableProps {
  columns: TableColumn[];
  rows: TableRow[];
  hasEmptySearch?: boolean;
  sort: ReadOnlyTableSortState;
  onToggleSortChange: (sortState: ReadOnlyTableSortState) => void;
  isLoadingMore?: boolean;
}

/**
 * Readonly table component
 * Displays a table with readonly data
 * Use sticky positioning for the header to use in scrolling containers
 *
 * @param param0 - Props for the table
 * @returns JSX.Element
 */
const ReadOnlyTable = ({
  columns,
  rows,
  hasEmptySearch,
  sort,
  isLoadingMore = false,
  onToggleSortChange,
}: ReadOnlyTableProps) => {
  const handleSortClick = (col: TableColumn) => {
    if (!sort || sort.direction === "off") {
      onToggleSortChange({
        columnId: col.id,
        direction: "asc",
      });
      return;
    }
    if (sort.columnId === col.id) {
      if (sort.direction === "asc") {
        onToggleSortChange({
          columnId: col.id,
          direction: "desc",
        });
      } else if (sort.direction === "desc") {
        onToggleSortChange({
          columnId: col.id,
          direction: "off",
        });
      } else {
        onToggleSortChange({
          columnId: col.id,
          direction: "asc",
        });
      }
    } else {
      onToggleSortChange({
        columnId: col.id,
        direction: "asc",
      });
    }
  };

  return (
    <table className="w-full">
      <thead>
        <tr>
          {columns.map((col, index) => (
            <th
              key={col.id}
              className={cn(
                "sticky top-0 z-1 text-left p-0",
                index === 0 ? "w-[128px] min-w-[128px]" : "",
              )}
            >
              <div className="relative">
                {/* Solid background layer for sticky */}
                <div className="absolute inset-0 bg-neutral-object z-0"></div>
                <div className="relative bg-neutral-object-accent-silent py-2 px-3 border-b border-r border-neutral flex justify-between items-center">
                  <div className="flex flex-col ">
                    <p className="text-xs text-body-loud uppercase flex truncate line-clamp-1">
                      {col.name || ""}
                    </p>
                    <span className="text-xs text-body-quiet font-normal truncate lowercase line-clamp-1">
                      {getColumnDisplayName(col.type)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="w-3 cursor-pointer"
                    onClick={() => handleSortClick(col)}
                  >
                    <Icon
                      type="fas"
                      name={getSortIcon(
                        sort.direction,
                        sort.columnId === col.id,
                      )}
                      className="text-on-disabled"
                    />
                  </button>
                </div>
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {!hasEmptySearch &&
          rows?.map((row, index) => (
            <tr
              key={`row-${row.id || row["row-key"]}-${index}`}
              className="bg-neutral-object"
            >
              {columns.map((col, cIndex) => (
                <td
                  key={`${col.id}-${index}-${row["row-key"]}`}
                  className={cn(
                    "text-body-loud text-sm p-0",
                    index % 2 !== 0 || cIndex === 0
                      ? "bg-neutral-object-accent-silent"
                      : "bg-neutral-object",
                  )}
                >
                  <div
                    className={cn(
                      "min-h-[48px] p-3 truncate line-clamp-1 h-full flex items-center border-neutral ",
                      cIndex === 0 ? "border-b border-r" : "",
                      index === rows.length - 1 ? "border-b" : "",
                    )}
                  >
                    {typeof row[col.id] === "boolean"
                      ? row[col.id]
                        ? "Verdadero"
                        : "Falso"
                      : row[col.id]}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        {hasEmptySearch && (
          <tr>
            <td
              colSpan={columns.length}
              className="text-center py-4 text-body-quiet h-[200px]"
            >
              Sin resultados
            </td>
          </tr>
        )}
      </tbody>
      {isLoadingMore && (
        <tfoot>
          <tr>
            <td colSpan={columns.length} className="bg-neutral-object">
              <p className="min-h-[48px] text-body-loud font-semibold flex justify-center items-center py-0.5 px-3 gap-4 ">
                <Icon
                  type="far"
                  name="spinner-third"
                  className="animate-spin text-xl text-body"
                />
                Cargando más datos...
              </p>
            </td>
          </tr>
        </tfoot>
      )}
    </table>
  );
};

export { ReadOnlyTable };
